import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, Users, Sparkles } from 'lucide-react';
import EventCountdown from './events/EventCountdown';
import PartnerStrip from './events/PartnerStrip';
import useEventCountdown from './events/useEventCountdown';
import { fetchEvents, eventPath } from '../utils/eventData';

const PromoCard = ({ event }) => {
  const { status, parts } = useEventCountdown(event.startISO, event.endISO);
  const [heroFailed, setHeroFailed] = useState(false);
  const promo = event.homePromo || {};

  if (status === 'finished') return null;

  const heroSrc = heroFailed ? event.heroImageFallback : event.heroImage;

  return (
    <section className="py-10 md:py-16 bg-white" data-testid="event-promo-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-3xl overflow-hidden bg-[#2d3a1a]">
          <img
            src={heroSrc}
            alt={event.heroImageAlt}
            onError={() => setHeroFailed(true)}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-45"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(135deg, rgba(45,58,26,0.95) 0%, rgba(45,58,26,0.78) 45%, rgba(61,74,38,0.55) 100%)',
            }}
          />

          <div className="relative p-6 sm:p-9 md:p-12">
            <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
              <p className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.2em] font-bold text-[#C8D843] bg-[#C8D843]/10 border border-[#C8D843]/30 rounded-full px-3 py-1.5">
                <Sparkles size={12} /> {promo.badge || 'Özel Etkinlik'}
              </p>
              <PartnerStrip partners={event.partners || []} onDark size="sm" testId="event-promo-partners" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-7 lg:gap-12 lg:items-end">
              <div className="lg:col-span-3">
                <h2
                  className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white leading-[1.1] mb-4"
                  style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '-0.015em' }}
                  data-testid="event-promo-title"
                >
                  {promo.title || event.name}
                </h2>
                <p className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[#C8D843] text-xs md:text-sm font-semibold mb-3">
                  <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} /> {event.dateLabel}</span>
                  <span className="inline-flex items-center gap-1.5 text-white/80"><Users size={14} /> {event.capacity} kişi</span>
                </p>
                <p className="text-white/80 text-sm md:text-base leading-relaxed max-w-xl">
                  {promo.subtitle}
                </p>
              </div>

              <div className="lg:col-span-2 flex flex-col gap-5">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/50 mb-2">
                    {status === 'upcoming' ? 'Başlamasına kalan süre' : 'Durum'}
                  </p>
                  <EventCountdown status={status} parts={parts} onDark testId="event-promo-countdown" />
                </div>
                <Link
                  to={eventPath(event)}
                  data-testid="event-promo-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#C8D843] text-[#1B1B1B] font-bold px-7 py-3.5 rounded-full text-sm md:text-base hover:bg-[#b8c83a] hover:gap-3 transition-all duration-300 w-full sm:w-auto lg:w-full"
                >
                  {promo.cta || 'Detayları İnceleyin'} <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/** Home page special-event block. Shows the soonest active event; hides itself when none. */
const EventPromo = () => {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    fetchEvents().then((list) => setEvent(list[0] || null));
  }, []);

  if (!event) return null;
  return <PromoCard event={event} />;
};

export default EventPromo;
